<template>
  <canvas width="400" height="200">
  </canvas>
</template>

<script>
  import { Chart } from 'chart.js'
  import numeral from 'numeral'

  const createGradient = (colors, $el) => {
    const ctx = $el.getContext('2d')
    const result = ctx.createLinearGradient(0, 0, 600, 0)
    for (const i in colors) {
      result.addColorStop(i, colors[i])
    }
    return result
  }

  const parseColors = (dataset, $el) => {
    const result = {}
    if (!dataset) {
      return null
    }
    if (dataset.borderColor instanceof Array) {
      result.borderColor = createGradient(dataset.borderColor, $el)
    }
    if (dataset.pointBackgroundColor instanceof Array) {
      result.pointBackgroundColor = createGradient(dataset.pointBackgroundColor, $el)
    }
    return result
  }

  export default {
    name: 'SimpleLineChart',
    props: {
      data: Object,
      type: String,
      yStepSize: {
        type: Number,
        default: 0
      },
      moneyFormatted: {
        type: Boolean,
        default: false
      }
    },
    computed: {
      computedData () {
        if (!this.data) {
          return null
        }

        return {
          ...this.data,
          datasets: this
            .data
            .datasets
            .map(dataset => ({
              // Modern line styling defaults (overridable per-dataset)
              tension: 0.35,
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHitRadius: 12,
              fill: false,
              ...dataset,
              ...parseColors(dataset, this.$el),
            })),
        }
      },
      // Format a value for the y-axis / tooltip
      formatValue () {
        return (value) => {
          if (value === null || value === undefined) {
            return value
          }

          if (this.moneyFormatted) {
            return numeral(value).format('$0.00a')
          }

          return Number.isFinite(value) ? numeral(value).format('0,0') : value
        }
      },
      options () {
        const formatValue = this.formatValue

        // Chart.js v3 options schema (the project ships chart.js 3.x).
        return {
          interaction: {
            mode: 'index',
            intersect: false,
          },
          layout: {
            padding: {
              top: 24,
              bottom: 8,
              left: 8,
              right: 8,
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              backgroundColor: 'rgba(17, 24, 39, 0.92)',
              titleColor: '#ffffff',
              bodyColor: '#ffffff',
              padding: 10,
              cornerRadius: 8,
              callbacks: {
                label: function (ctx) {
                  const label = ctx.dataset.label ? `${ctx.dataset.label}: ` : ''
                  return label + formatValue(ctx.parsed.y)
                },
              },
            },
          },
          scales: {
            x: {
              grid: {
                display: false,
                drawBorder: false,
              },
              ticks: {
                color: '#9ca3af',
                maxRotation: 0,
                autoSkip: true,
                font: { size: 11 },
              },
            },
            y: {
              beginAtZero: true,
              grid: {
                color: '#f3f4f6',
                drawBorder: false,
              },
              ticks: {
                color: '#9ca3af',
                padding: 6,
                stepSize: this.yStepSize || undefined,
                font: { size: 11 },
                callback: function (value) {
                  return formatValue(value)
                },
              },
            },
          },
        }
      }
    },
    mounted () {
      this.chart = new Chart(this.$el, {
        type: this.type || 'line',
        data: this.computedData,
        options: this.options
      })
    },
    watch: {
      // Upon changing the data, this element is not redrawn (i.e. moving from one "info" actor tab to another using the "next actor button" will keep the same chart as the one of the actor before)
      data (data) {
        this.chart.destroy()

        this.chart = new Chart(this.$el, {
          type: this.type || 'line',
          data: this.computedData,
          options: this.options
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
  canvas {
    background-color: transparent;
  }
</style>
